import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
import CardListHeading from "@insite/content-library/Components/CardListHeading";
import { QuoteSummaryCardStyles } from "@insite/content-library/Components/QuoteSummaryCard";
import Clickable from "@insite/mobius/Clickable";
import DataTable from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderPresentationProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import downloadReportHistory from "@tKgpco/Store/Data/ReportHistory/Handler/DownloadReportHistory";
import loadReportHistory from "@tKgpco/Store/Data/ReportHistory/Handler/LoadReportHistory";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    format = "format",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.format]: string;
    };
}

const mapStateToProps = (state: CustomApplicationState) => {
    return {
        currentPage: getCurrentPage(state).type,
        reportsLoading: state.KGPState.ReportHistoryStore.isLoading,
        isDashBoardData: state.KGPState.ReportHistoryStore.reportHistoryParams?.isFromDashboard,
        orderHistortReports: state.KGPState.ReportHistoryStore.reportHistoryCollection?.orderHistoryReports,
        pagination: state.KGPState.ReportHistoryStore.reportHistoryCollection?.pagination,
        canshowPrice: state.context?.session?.properties?.canShowPrice?.toLowerCase() === "true",
        featurePages: state.context.session.properties.featurePages,
    };
};

const mapDispatchToProps = {
    loadReportHistory,
    downloadReportHistory,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentQuotesStyles {
    cardList?: CardListStyles;
    noReportsGridItem?: GridItemProps;
    noReportsText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    quoteSummaryCard?: QuoteSummaryCardStyles;
    reportsContainer?: InjectableCss;
    reportContent?: InjectableCss;
    fileName?: TypographyPresentationProps;
    tableContainer?: InjectableCss;
    downloadCell?: DataTableCellProps;
    downloadHead?: DataTableHeaderPresentationProps;
    pagination?: PaginationPresentationProps;
}

export const recentQuotesStyles: RecentQuotesStyles = {
    cardList: {
        gridContainer: {
            css: css`
                border: 1px solid ${getColor("common.border")};
                margin-bottom: 20px;
            `,
        },
    },
    noReportsGridItem: {
        width: 12,
        css: css`
            justify-content: center;
        `,
    },
    reportsContainer: {
        css: css`
            padding: 0 20px 0px 35px;
            width: 100%;
        `,
    },
    fileName: {
        color: "primary",
    },
    reportContent: {
        css: css`
            display: flex;
            gap: 10px;
            width: 100%;
            margin-bottom: 10px;
        `,
    },
    downloadHead: {
        css: css`
            text-align: center;
        `,
    },
    downloadCell: {
        css: css`
            button {
                margin: 0 auto;
            }
        `,
    },
    tableContainer: {
        css: css`
            width: 100%;
            overflow: auto;
            margin-top: 20px;
        `,
    },
};

const styles = recentQuotesStyles;

const ReportDownload = ({
    fields,
    id,
    currentPage,
    loadReportHistory,
    reportsLoading,
    orderHistortReports,
    isDashBoardData,
    downloadReportHistory,
    pagination,
    canshowPrice,
    featurePages,
}: Props) => {
    const isDashBoardPage = currentPage === "MyAccountPage";
    const [downloadingFilesIds, setDownloadingFilesIds] = React.useState<string[] | null>(null);
    const [page, setPage] = React.useState(pagination?.page || 1);
    const dateTime = new Date();

    React.useEffect(() => {
        if (isDashBoardData !== isDashBoardPage || pagination?.page !== page) {
            loadReportHistory({
                pageNumber: page,
                isFromDashboard: isDashBoardPage,
                currentDateTime: dateTime.toString(),
            });
        }
    }, [isDashBoardData, page]);

    if (!canshowPrice || featurePages?.includes("Download Report History")) {
        return null;
    }

    const downloadReport = (jobId: string, downloadFileName: string) => {
        setDownloadingFilesIds([...(downloadingFilesIds || []), jobId]);
        downloadReportHistory({
            jobId,
            downloadFileName,
            onSuccess: () => {
                removeFileFromDownloading(jobId);
            },
            onError: () => {
                removeFileFromDownloading(jobId);
            },
        });
    };

    const removeFileFromDownloading = (downloadFileNameId: string) => {
        const files = downloadingFilesIds?.filter(fileNameId => fileNameId !== downloadFileNameId);
        setDownloadingFilesIds(files || []);
    };

    const changePage = (newPageIndex: number) => {
        setPage(newPageIndex);
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
    };

    if (fields.format === "list") {
        return (
            <CardList
                css={css`
                    padding-bottom: 50px;
                `}
                extendedStyles={styles.cardList}
            >
                <CardListHeading
                    heading={translate("Report Downloads")}
                    viewAllUrl="/MyAccount/DownloadReportHistory"
                />
                {reportsLoading ? (
                    <GridItem {...styles.noReportsGridItem}>
                        <LoadingSpinner size={20} />
                    </GridItem>
                ) : orderHistortReports?.length ? (
                    <StyledWrapper {...styles.reportsContainer}>
                        {orderHistortReports.map(report => (
                            <StyledWrapper key={report.id} {...styles.reportContent}>
                                <Typography {...styles.fileName}>{report.downloadFileName}</Typography>
                                <Clickable
                                    title={`Download ${report.downloadFileName}`}
                                    onClick={() => downloadReport(report.jobId, report.downloadFileName)}
                                >
                                    {downloadingFilesIds?.includes(report.jobId) ? (
                                        <LoadingSpinner size={20} color="primary" />
                                    ) : (
                                        <Icon src="Download" size={20} color="primary" />
                                    )}
                                </Clickable>
                            </StyledWrapper>
                        ))}
                    </StyledWrapper>
                ) : (
                    <GridItem {...styles.noReportsGridItem}>
                        <Typography {...styles.noReportsText}>{translate("No Reports Found")}</Typography>
                    </GridItem>
                )}
            </CardList>
        );
    }

    return (
        <StyledWrapper>
            {reportsLoading ? (
                <GridItem {...styles.noReportsGridItem}>
                    <LoadingSpinner />
                </GridItem>
            ) : orderHistortReports?.length ? (
                <>
                    <StyledWrapper {...styles.tableContainer}>
                        <DataTable>
                            <DataTableHead>
                                <DataTableHeader>{translate("File Name")}</DataTableHeader>
                                <DataTableHeader {...styles.downloadHead}>
                                    {translate("Download Report")}
                                </DataTableHeader>
                            </DataTableHead>
                            <DataTableBody>
                                {orderHistortReports.map(report => (
                                    <DataTableRow key={report.id}>
                                        <DataTableCell>{report.downloadFileName}</DataTableCell>
                                        <DataTableCell {...styles.downloadCell}>
                                            <Clickable
                                                title={`Download ${report.downloadFileName}`}
                                                onClick={() => downloadReport(report.jobId, report.downloadFileName)}
                                            >
                                                {downloadingFilesIds?.includes(report.jobId) ? (
                                                    <LoadingSpinner size={30} color="primary" />
                                                ) : (
                                                    <Icon src="Download" size={30} color="primary" />
                                                )}
                                            </Clickable>
                                        </DataTableCell>
                                    </DataTableRow>
                                ))}
                            </DataTableBody>
                        </DataTable>
                    </StyledWrapper>
                    {pagination ? (
                        <Pagination
                            {...styles.pagination}
                            resultsCount={pagination.totalItemCount}
                            currentPage={pagination.page}
                            resultsPerPage={pagination.pageSize}
                            resultsPerPageOptions={pagination.pageSizeOptions}
                            onChangePage={changePage}
                            onChangeResultsPerPage={changeResultsPerPage}
                            prevPage={pagination.prevPageUri}
                            nextPage={pagination.nextPageUri}
                        />
                    ) : null}
                </>
            ) : (
                <GridItem {...styles.noReportsGridItem}>
                    <Typography {...styles.noReportsText}>{translate("No Reports Found")}</Typography>
                </GridItem>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ReportDownload),
    definition: {
        displayName: "Download Reports",
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.format,
                displayName: "Format Type",
                editorTemplate: "DropDownField",
                hideEmptyOption: true,
                options: [
                    { value: "list", displayName: "List" },
                    { value: "table", displayName: "Table" },
                ],
                defaultValue: "list",
                fieldType: "General",
                sortOrder: 0,
            },
        ],
    },
};

export default widgetModule;
